import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "./ui";
import { api } from "../lib/api";
import { ApiError } from "../lib/apiError";
import { flag } from "../lib/flag";
import { pollCountdown } from "../lib/format";
import { VerifyAccount } from "./VerifyAccount";
import type { GenericPoll } from "@shared/types";
import { useT } from "../i18n";

// Tap-to-vote community poll. Each option is a row with a fill bar; tapping casts
// (or changes) your vote — re-voting allowed. Results show after you vote.
// `scope` shows which nation the poll belongs to (or 🌍 global) — pass it where a
// poll can appear out of its own context (e.g. the "other nations" list).
// `linkToDetail` wraps the question in a link to the shareable /polls/:id page —
// pass it from list views, omit it on the detail page itself.
export function PollWidget({
  poll,
  scope,
  linkToDetail = false,
}: {
  poll: GenericPoll;
  scope?: { name: string; code: string } | "global";
  linkToDetail?: boolean;
}) {
  const t = useT();
  const [state, setState] = useState<GenericPoll>(poll);
  // Seed from the server's recall so a returning voter sees their pick + results.
  const [myVote, setMyVote] = useState<string | null>(poll.myVote);
  const [busy, setBusy] = useState(false);
  // A rejected vote, surfaced inline (not an alert) so the message + a verify CTA
  // can render under the options. `pending` remembers the option so a successful
  // verification can retry it transparently.
  const [voteError, setVoteError] = useState<{ message: string; canVerify: boolean } | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [showVerify, setShowVerify] = useState(false);
  const voted = myVote !== null;
  const closed = state.endDate != null && state.endDate <= Date.now();
  // A closed poll shows its tallies to everyone (no need to have voted), and the
  // leading option is crowned. Ties resolve to the first option by position.
  const showResults = voted || closed;
  const winnerId = closed && state.total > 0 ? state.options.reduce((a, b) => (b.votes > a.votes ? b : a)).id : null;

  function describeError(e: unknown): { message: string; canVerify: boolean } {
    if (e instanceof ApiError && e.code === "needs_verification") {
      return { message: t("polls.needsVerification"), canVerify: true };
    }
    if (e instanceof ApiError && e.code === "level_too_low") {
      return {
        message: t("polls.levelTooLow", { required: e.requiredLevel ?? state.minLevel ?? 0, current: e.currentLevel ?? 0 }),
        canVerify: false,
      };
    }
    return { message: String((e as Error).message), canVerify: false };
  }

  async function vote(optionId: string) {
    if (busy || closed) return;
    setBusy(true);
    setVoteError(null);
    try {
      const updated = await api.votePoll(state.id, optionId);
      setState(updated);
      setMyVote(optionId);
      setShowVerify(false);
      setPending(null);
    } catch (e) {
      setPending(optionId);
      setVoteError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  async function closeNow() {
    if (busy) return;
    setBusy(true);
    try {
      setState(await api.closePoll(state.id));
    } catch (e) {
      setVoteError({ message: String((e as Error).message), canVerify: false });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-disp font-bold text-[17px] leading-snug">
          {linkToDetail ? (
            <Link to={`/polls/${state.id}`} className="hover:text-accent transition">{state.question}</Link>
          ) : (
            state.question
          )}
        </h3>
        <div className="flex items-center gap-1 shrink-0">
          {(state.requireVerified || state.minLevel != null) && (
            <span className="tag font-mono text-[10px] text-cyan uppercase tracking-wider">
              {state.minLevel != null ? t("polls.badgeLevel", { n: state.minLevel }) : t("polls.badgeVerified")}
            </span>
          )}
          {state.tag && <span className="tag font-mono text-[10px] text-dim uppercase tracking-wider">{state.tag}</span>}
        </div>
      </div>
      {scope && (
        <div className="eyebrow normal-case mt-1">
          {scope === "global" ? `🌍 ${t("polls.global")}` : <>{flag(scope.code)} {scope.name}</>}
        </div>
      )}
      <div className="font-mono text-[11px] mt-1 mb-2 flex items-center justify-between gap-2">
        <span className="text-accent">
          {closed
            ? `🔒 ${t("polls.closedVotes", { n: state.total })}`
            : voted
              ? `✓ ${t("polls.votesTap", { n: state.total })}`
              : t("polls.tapToVote")}
        </span>
        {!closed && state.endDate != null && (
          <span className="text-dim normal-case">{pollCountdown(state.endDate, t)}</span>
        )}
      </div>

      {state.options.map((o) => {
        const pct = state.total > 0 ? Math.round((o.votes / state.total) * 100) : 0;
        const mine = myVote === o.id;
        const won = o.id === winnerId;
        return (
          <button
            key={o.id}
            type="button"
            disabled={busy || closed}
            onClick={() => vote(o.id)}
            className={`poll-opt w-full text-left ${mine ? "voted" : ""} ${won ? "ring-1 ring-accent rounded" : ""} disabled:cursor-default`}
          >
            {showResults && <span className="fill" style={{ width: `${pct}%` }} />}
            <span className="radio">{mine && <span className="text-[#06120f] text-[12px] font-black">✓</span>}</span>
            <span className="flex-1 font-disp text-[15px]">
              {o.label}
              {won && <span className="ml-1 text-cyan text-[11px] font-mono uppercase">🏆 {t("polls.winner")}</span>}
            </span>
            {showResults && <span className={`font-mono text-[13px] font-bold ${mine ? "text-cyan" : "text-muted"}`}>{pct}%</span>}
          </button>
        );
      })}

      {state.closeable && (
        <button onClick={closeNow} disabled={busy} className="text-red-400 text-xs mt-2 font-semibold disabled:opacity-50">
          {t("polls.closeNow")}
        </button>
      )}

      {voteError && (
        <div className="mt-2 text-sm">
          <p className="text-red-400">{voteError.message}</p>
          {voteError.canVerify && !showVerify && (
            <button onClick={() => setShowVerify(true)} className="text-accent text-sm mt-1 font-semibold">
              {t("polls.verifyCta")}
            </button>
          )}
          {showVerify && (
            <VerifyAccount
              onVerified={() => {
                // Token now stored — retry the vote they were blocked on.
                if (pending) void vote(pending);
              }}
            />
          )}
        </div>
      )}
    </Card>
  );
}
